import React, { useEffect, useState, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Typography, Box } from '@mui/material';
import { SmartFormStates } from './SmartForm';
import ErrorWrapper from '@component/ErrorWrapper';

const SmartRichTextEditor = ({
  field,
  label,
  value,
  onChange,
  isEdit,
  sx = {},
  renderView,
  editable = true,
  validator,
  setValuesAreValid,
  formState,
  ...props
}) => {
  const [content, setContent] = useState(value || '');
  const [showError, setShowError] = useState(false);
  const [validatorResult, setValidatorResult] = useState("");

  // Default modules for the editor
  // zvetsit charcount
  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' },
      { 'list': 'bullet' },
      // { 'indent': '-1' },
      // { 'indent': '+1' }
    ],
      ['link'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    },
  }), []);

  // Default formats for the editor
  const formats = [
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link'
  ];

  useEffect(() => {
    setShowError(formState === SmartFormStates.SAVING);
  }, [formState]);

  useEffect(() => {
    if (validator) {
      const validatorResult = validator(content);
      setValuesAreValid(!validatorResult);
      setValidatorResult(validatorResult);
    }
  }, [content, validator, setValuesAreValid]);

  const handleChange = (content, delta, source, editor) => {
    const json = editor.getContents();
    setContent(json);
    onChange(field, json);
  };

  // Convert Delta to HTML for rendering
  const deltaToHtml = (delta) => {
    const tempQuill = new Quill(document.createElement('div'));
    tempQuill.setContents(delta);
    return tempQuill.root.innerHTML;
  };

  // Function to safely render content from Delta
  const renderDeltaContent = (value) => {
    let delta = value;
    if (typeof value === 'string') {
      try {
        delta = JSON.parse(value);
      } catch {
        return null;
      }
    }

    const html = deltaToHtml(delta);
    return (
      <div
        dangerouslySetInnerHTML={{ __html: html }}
        style={{
          maxWidth: '100%',
          overflow: 'hidden',
          wordBreak: 'break-word'
        }}
      />
    );
  };

  // Get initial value in correct format
  const getInitialValue = () => {
    if (!value) return '';

    if (typeof value === 'string') {
      try {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(value);
        return parsed;
      } catch {
        return value;
        // If not JSON, convert to Delta format
        // const tempQuill = new Quill(document.createElement('div'));
        // tempQuill.clipboard.dangerouslyPasteHTML(value);
        // return tempQuill.getContents();
      }
    }
    return value; // Assume it's already Delta format
  };

  return (
    <Box sx={sx}>
      {!isEdit || !editable ? (
        renderView ? (
          renderView(value)
        ) : (
          <>
            {label && (
              <Typography
                variant="caption"
                component="div"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  lineHeight: 1.66,
                  mb: 0.5
                }}
              >
                {label}
              </Typography>
            )}
            {value ? renderDeltaContent(value) : <Typography sx={{ border: 1 }} variant="body1">-</Typography>}
          </>
        )
      ) : (
        <ErrorWrapper message={validatorResult} showMessage={showError}>
          <Box sx={{
            '& .ql-container': {
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderTop: 'none',
              minHeight: '200px'
            },
            '& .ql-toolbar': {
              borderTopLeftRadius: '4px',
              borderTopRightRadius: '4px',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderBottom: 'none'
            },
            '& .ql-editor': {
              minHeight: '200px'
            },
            mt: 2
          }}>
            {label && <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>}
            <ReactQuill
              theme="snow"
              value={getInitialValue()}
              onChange={handleChange}
              modules={modules}
              formats={formats}
              {...props}
            />
          </Box>
        </ErrorWrapper>
      )}
    </Box>
  );
};

export default SmartRichTextEditor;